// storedFileUtils.ts
import type { StoredFile } from "hooks/useProcessFiles";

/**
 * 从任意图片URL创建StoredFile对象
 * @param imageUrl 图片URL
 * @returns Promise<StoredFile>
 */
export async function createStoredFileFromUrl(
  imageUrl: string
): Promise<StoredFile> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const urlParts = imageUrl.split("/");
    const fileName = urlParts[urlParts.length - 1];
    const extension = fileName.split(".").pop()?.toLowerCase() || undefined;

    const file = new File([blob], fileName, { type: blob.type });
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    const storedFile: StoredFile = {
      file: file,
      preview: imageUrl,
      id: id,
      extension: extension,
      isGenerated: false,
    };

    return storedFile;
  } catch (error) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return {
      file: new File([], "error-file"),
      preview: imageUrl,
      id: id,
      error: error instanceof Error ? error.message : "Failed to fetch image",
      isGenerated: false,
    };
  }
}

/**
 * 从项目 public 目录下的图片路径创建 StoredFile 对象
 * @param publicPath public 目录下的相对路径（例如 "/images/photo.jpg"）
 * @returns Promise<StoredFile>
 */
export async function createStoredFileFromPublicDir(
  publicPath: string
): Promise<StoredFile> {
  try {
    // 转换为完整的 URL（假设运行在 Web 环境中）
    const fullUrl = `${window.location.origin}${publicPath}`;

    // 获取文件内容
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to load file from public directory: ${response.status}`
      );
    }
    const blob = await response.blob();

    // 提取文件名和扩展名
    const pathParts = publicPath.split("/");
    const fileName = pathParts[pathParts.length - 1] || "image";
    const extensionMatch = fileName.match(/\.([0-9a-z]+)$/i);
    const extension = extensionMatch
      ? extensionMatch[1].toLowerCase()
      : undefined;

    // 创建 File 对象
    const file = new File([blob], fileName, {
      type: blob.type || "image/jpeg",
    });

    // 生成唯一 ID
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    const storedFile: StoredFile = {
      file: file,
      preview: fullUrl, // 使用完整 URL 作为预览
      id: id,
      extension: extension,
      isGenerated: false,
    };

    return storedFile;
  } catch (error) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return {
      file: new File([], "error-file"),
      preview: publicPath,
      id: id,
      error:
        error instanceof Error
          ? error.message
          : "Failed to process public directory file",
      isGenerated: false,
    };
  }
}

/**
 * 将 Base64 图片字符串转换为 StoredFile 对象
 * @param image Base64 格式的图片字符串 (data URL)
 * @returns StoredFile
 */
export const convertImageToStoredFile = (image: string): StoredFile => {
  try {
    // 解析 data URL
    const [prefix, base64Data] = image.split(",");
    const mimeType = prefix.match(/data:([^;]+)/)?.[1] || "";

    // 从 mimeType 中提取扩展名
    const extensionMatch = mimeType.split("/")[1];
    const extension = extensionMatch ? extensionMatch.toUpperCase() : undefined;

    // 生成唯一 ID
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // 创建 File 对象
    const file = new File(
      [atob(base64Data)],
      `image.${extension?.toLowerCase() || "unknown"}`,
      {
        type: mimeType,
      }
    );

    return {
      file,
      preview: image,
      id,
      extension,
      isGenerated: true,
      error: undefined,
    };
  } catch (error) {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return {
      file: new File([], "error-file"),
      preview: image,
      id,
      error:
        error instanceof Error
          ? error.message
          : "Failed to convert base64 image",
      isGenerated: true,
    };
  }
};
